import { FC, ReactNode, useReducer, useEffect } from 'react';
import { StreamChat } from 'stream-chat';
import { Chat, Channel, LoadingIndicator } from 'stream-chat-react';

import { api } from '@getstream/ui-library/build';

import Message from '../Message';
import DateSeparator from '../DateSeparator';
import ChannelInner from '../ChannelInner';
import CustomInput from '../CustomInput';

import Feedback from '../../Shared/Feedback';

const { apiGetPublicUserToken } = api;

import { chatReducer } from './ChatController.reducer';
import { initialState } from './ChatController.state';
import { ChatContextProvider } from './ChatController.context';

import { Actions } from '../../../types/chat';

import styles from './ChatController.module.css';

export type Props = {
  children: ReactNode | undefined;
};
export const ChatController: FC<Props> = ({ children }) => {
  const [state, dispatch] = useReducer(chatReducer, initialState);

  useEffect(() => {
    if (typeof window === 'undefined') return; // Skip async for SSR

    (async () => {
      try {
        const { api_key, user_id, user_token } = await apiGetPublicUserToken(
          import.meta.env.VITE_API_BASEURL,
        );
        dispatch({
          type: Actions.SetClientUser,
          apiKey: api_key,
          userId: user_id,
          userToken: user_token,
          userName: user_id.split('-')[0],
        });
      } catch (err) {
        window.Sentry?.captureException(err);
      }
    })();
  }, []);

  const setClient = async () => {
    try {
      if (state.client) return;
      const client = StreamChat.getInstance(state.apiKey || '');
      await client.connectUser(
        {
          id: state.userId,
          name: state.userName,
          image: 'https://bit.ly/2u9Vc0r',
        },
        state.userToken,
      );
      dispatch({ type: Actions.SetClient, client });
    } catch (error) {
      window.Sentry?.captureException(error);
      dispatch({ type: Actions.SetError, error });
    }
  };

  const setLoading = (loading: boolean) =>
    dispatch({ type: Actions.SetLoading, loading });

  const setChannel = () => {
    try {
      if (state.channel) return;
      const channel = state.client.channel('messaging', state.channelId, {
        name: 'Private Chat About the Kingdom',
        image: 'https://bit.ly/2F3KEoM',
        members: [state.userId],
        session: 8,
      });
      dispatch({ type: Actions.SetChannel, channel });
    } catch (error) {
      dispatch({ type: Actions.SetError, error });
    }
  };

  const sendMessage = async () => {
    try {
      const { message } = await state.channel.sendMessage({
        text: 'Did you already see the trailer? https://www.youtube.com/watch?v=wA38GCX4Tb0',
      });
      dispatch({ type: Actions.SendMessage, message });
    } catch (error) {
      dispatch({ type: Actions.SetError, error });
    }
  };

  const addReaction = async () => {
    try {
      const reaction = await state.channel.sendReaction(state.message.id, {
        type: 'love',
      });

      dispatch({ type: Actions.AddReaction, reaction });
    } catch (error) {
      dispatch({ type: Actions.SetError, error });
    }
  };

  const startThread = async () => {
    try {
      const parent = await state.channel.sendMessage({
        text: 'Episode 1 just blew my mind!',
      });
      await state.channel.sendMessage({
        text: 'stop it, no spoilers please!',
        parent_id: parent.message.id,
      });
    } catch (error) {
      dispatch({ type: Actions.SetError, error });
    }
  };

  const sendListeningMessage = async () => {
    try {
      await state.channel.sendMessage({
        text: 'what is the medieval equivalent of tabs vs spaces?',
      });
    } catch (error) {
      dispatch({ type: Actions.SetError, error });
    }
  };

  const startListening = () => {
    dispatch({ type: Actions.StartListening });
  };

  useEffect(() => {
    if (state.channel && state.listening) {
      const showEvent = (event: any) =>
        dispatch({ type: Actions.NewEvent, event });
      state.channel.on('message.new', showEvent);
      return () => {
        state.channel?.off('message.new', showEvent);
      };
    }
  }, [state.channel, state.listening]);

  useEffect(() => {
    if (state.feedback) {
      const removeFeedbackTimer = setTimeout(
        () => dispatch({ type: Actions.ClearFeedback }),
        3000,
      );
      return () => {
        clearTimeout(removeFeedbackTimer);
      };
    }
  }, [state.feedback]);

  return (
    <div className={styles.chatContext}>
      <ChatContextProvider
        state={state}
        value={{
          startListening,
          sendListeningMessage,
          startThread,
          addReaction,
          sendMessage,
          setChannel,
          setLoading,
          setClient,
        }}
      >
        {children}
        <div className={styles.chatSidebar}>
          <div className={styles.chatContainer}>
            {state.feedback && (
              <Feedback type={state.feedback.type}>
                {state.feedback.msg}
              </Feedback>
            )}

            {state.client && state.channel ? (
              <Chat client={state.client} theme={'team dark'}>
                <Channel
                  channel={state.channel}
                  Input={CustomInput}
                  Message={Message}
                  EmptyStateIndicator={() => <div />}
                  DateSeparator={DateSeparator}
                >
                  <ChannelInner />
                </Channel>
              </Chat>
            ) : (
              <div className={styles.chatInitialState}>
                <div className={styles.chatInitialStateMessage}>
                  <LoadingIndicator size={30} />
                  <p>
                    {!state.client
                      ? 'Waiting to initialize'
                      : 'Waiting for Channel'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </ChatContextProvider>
    </div>
  );
};
